import React, { useState, useRef, useMemo, useEffect } from 'react';
import { fetchAboneParklar, fetchHistory, fetchHistoryItem, fetchDbStatus } from '../../services/askiService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const COLUMN_LABELS = {
  ABONENO: 'Abone No', ABONE_ADI: 'Abone / Park', ILCE_ADI: 'Ilce',
  MAHALLE_ADI: 'Mahalle', SOKAK_ADI: 'Sokak', DONEM: 'Donem',
  M3_TUKETIM: 'Tuketim (m\u00B3)', SAYAC_TURU: 'Sayac Turu', SAYAC_CAPI: 'Sayac Capi',
  ABONETIPI: 'Abone Tipi', ABONETIPI_ANAGRUP: 'Ana Grup', KAYNAK: 'Kaynak',
};
const TABLE_COLUMNS = ['ABONENO','ABONE_ADI','ILCE_ADI','MAHALLE_ADI','SOKAK_ADI','DONEM','M3_TUKETIM','KAYNAK','ABONETIPI','ABONETIPI_ANAGRUP','SAYAC_TURU','SAYAC_CAPI'];

const C = { bg: '#f8fafc', card: '#fff', border: '#e2e8f0', borderLight: '#f1f5f9', text: '#1e293b', textMuted: '#64748b', textFaint: '#94a3b8', accent: '#2563eb', accentLight: '#dbeafe', accentBg: '#eff6ff', green: '#059669', greenBg: '#ecfdf5', amber: '#d97706', amberBg: '#fffbeb', red: '#dc2626', redBg: '#fef2f2' };

const formatDonem = (d) => { const s = String(d).replace('.0','').replace(/\..*/,''); if(s.length>=6){const y=s.substring(0,4),m=parseInt(s.substring(4),10),mn=['','Oca','Sub','Mar','Nis','May','Haz','Tem','Agu','Eyl','Eki','Kas','Ara'];return `${mn[m]||m} ${y}`;}return s;};
const formatDate = (iso) => { try{return new Date(iso).toLocaleString('tr-TR',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'});}catch{return iso;}};
const formatSize = (b) => { if(b>1048576)return `${(b/1048576).toFixed(1)} MB`;if(b>1024)return `${(b/1024).toFixed(0)} KB`;return `${b} B`;};

const ASKILiveData = () => {
  const [data, setData] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showRaw, setShowRaw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [filterIlce, setFilterIlce] = useState('');
  const [sortCol, setSortCol] = useState('DONEM');
  const [sortDir, setSortDir] = useState('desc');
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeHistoryKey, setActiveHistoryKey] = useState(null);
  const [historyItemLoading, setHistoryItemLoading] = useState(false);
  const [bgProcessing, setBgProcessing] = useState(false);
  const pollRef = useRef(null);
  const [dbSyncInfo, setDbSyncInfo] = useState(null);
  const [dbStatus, setDbStatus] = useState(null);
  const fetchLock = useRef(false);
  const [selectedPark, setSelectedPark] = useState(null);
  const [chartType, setChartType] = useState('line');

  const loadHistory = async () => { setHistoryLoading(true); try { const r = await fetchHistory(); setHistoryList(r.items||[]); } catch(e){ console.warn('Gecmis yuklenemedi:',e.message); } finally { setHistoryLoading(false); } };
  const loadDbStatus = async () => { try { const r = await fetchDbStatus(); setDbStatus(r); } catch(e){ console.warn('DB status yuklenemedi:',e.message); } };
  useEffect(() => { loadHistory(); loadDbStatus(); }, []);

  const isNetworkError = (msg) => /ETIMEDOUT|ECONNREFUSED|ENOTFOUND|ECONNRESET|network error|SOAP network|connect timed out|Failed to fetch|NetworkError|zaman asimi|timeout/i.test(String(msg || ''));

  const loadLatestFromHistory = async (list) => {
    const items = list || historyList;
    if (!items.length) {
      try {
        const hist = await fetchHistory();
        const fresh = hist.items || [];
        setHistoryList(fresh);
        if (!fresh.length) return false;
        const latest = fresh[0];
        const r = await fetchHistoryItem(latest.key);
        setData(r.data);
        setFetchedAt(r.fetchedAt || latest.date);
        setActiveHistoryKey(latest.key);
        return true;
      } catch (e) {
        console.warn('Son gecmis yuklenemedi:', e.message);
        return false;
      }
    }
    try {
      const latest = items[0];
      const r = await fetchHistoryItem(latest.key);
      setData(r.data);
      setFetchedAt(r.fetchedAt || latest.date);
      setActiveHistoryKey(latest.key);
      return true;
    } catch (e) {
      console.warn('Son gecmis yuklenemedi:', e.message);
      return false;
    }
  };

  const stopPolling = () => { if(pollRef.current){clearInterval(pollRef.current);pollRef.current=null;} };
  const startBgPolling = (prevHistoryCount) => {
    setBgProcessing(true); setError(null);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const hist = await fetchHistory();
        const newItems = hist.items || [];
        if (newItems.length > prevHistoryCount) {
          stopPolling(); setBgProcessing(false);
          setHistoryList(newItems);
          loadDbStatus();
          const latest = newItems[0];
          if (latest) {
            try { const r = await fetchHistoryItem(latest.key); setData(r.data); setFetchedAt(r.fetchedAt||latest.date); } catch(e) { /* ignore */ }
          }
          setDbSyncInfo({ total: newItems.length, inserted: newItems.length - prevHistoryCount });
        }
        if (attempts >= 30) {
          stopPolling();
          setBgProcessing(false);
          await loadLatestFromHistory(newItems);
        }
      } catch(e) { /* keep polling */ }
    }, 10000);
  };
  useEffect(() => () => stopPolling(), []);

  const doFetch = async () => {
    if (fetchLock.current) return;
    fetchLock.current = true;
    setLoading(true);
    setError(null);
    setShowConfirm(false);
    setActiveHistoryKey(null);
    const prevCount = historyList.length;
    try {
      const result = await fetchAboneParklar();
      if (result.status === 'processing') {
        setLoading(false);
        startBgPolling(prevCount);
        fetchLock.current = false;
        return;
      }
      setData(result.data);
      setFetchedAt(result.fetchedAt || new Date().toISOString());
      setDbSyncInfo(result.dbSync || null);
      if (result.dbSyncError) console.warn('DB sync hatasi:', result.dbSyncError);
      loadHistory();
      loadDbStatus();
    } catch (err) {
      console.warn('ASKI cekim:', err.message);
      setLoading(false);
      fetchLock.current = false;
      if (isNetworkError(err.message)) {
        // Ag / ASKİ erisim hatasi kullaniciya gosterilmez; son kayitli veriye dus
        await loadLatestFromHistory();
        loadDbStatus();
        return;
      }
      // API Gateway kesintisi: Lambda arka planda bitmis olabilir
      startBgPolling(prevCount);
      return;
    } finally {
      if (!bgProcessing) {
        setLoading(false);
        fetchLock.current = false;
      }
    }
  };
  const loadHistoryItem = async (item) => {
    setHistoryItemLoading(true);
    setActiveHistoryKey(item.key);
    setError(null);
    try {
      const r = await fetchHistoryItem(item.key);
      setData(r.data);
      setFetchedAt(r.fetchedAt || item.date);
    } catch (err) {
      console.warn('Gecmis veri yuklenemedi:', err.message);
      if (!isNetworkError(err.message)) setError('Gecmis veri yuklenemedi.');
    } finally {
      setHistoryItemLoading(false);
    }
  };
  const handleFetchClick = () => { if(data&&!showConfirm){setShowConfirm(true);return;} doFetch(); };

  const stats = useMemo(() => {
    if(!data||data.length===0)return null;
    const ilceler=[...new Set(data.map(r=>r.ILCE_ADI).filter(Boolean))];
    const aboneler=[...new Set(data.map(r=>r.ABONENO).filter(Boolean))];
    const toplamTuketim=data.reduce((s,r)=>s+(parseFloat(r.M3_TUKETIM)||0),0);
    const donemler=[...new Set(data.map(r=>String(r.DONEM).replace('.0','').replace(/\..*/,'')).filter(Boolean))].sort();
    return{ilceler,aboneler,toplamTuketim,donemler,kayitSayisi:data.length};
  },[data]);

  const filtered = useMemo(() => {
    if(!data)return[];let rows=[...data];
    if(filterIlce)rows=rows.filter(r=>r.ILCE_ADI===filterIlce);
    if(search.trim()){const q=search.toLowerCase();rows=rows.filter(r=>Object.values(r).some(v=>String(v).toLowerCase().includes(q)));}
    rows.sort((a,b)=>{let va=a[sortCol],vb=b[sortCol];if(sortCol==='M3_TUKETIM'||sortCol==='SAYAC_CAPI'||sortCol==='DONEM'){va=parseFloat(va)||0;vb=parseFloat(vb)||0;}else{va=String(va||'');vb=String(vb||'');}if(va<vb)return sortDir==='asc'?-1:1;if(va>vb)return sortDir==='asc'?1:-1;return 0;});
    return rows;
  },[data,filterIlce,search,sortCol,sortDir]);

  const handleSort=(col)=>{if(sortCol===col)setSortDir(d=>d==='asc'?'desc':'asc');else{setSortCol(col);setSortDir('desc');}};
  const cellValue=(row,col)=>{const val=row[col];if(val===null||val===undefined)return'-';if(col==='DONEM')return formatDonem(val);if(col==='M3_TUKETIM')return`${parseFloat(val).toLocaleString('tr-TR')} m\u00B3`;if(col==='SAYAC_CAPI')return`\u00D8${val}`;return String(val);};

  const parkChartData = useMemo(() => {
    if (!selectedPark || !data) return [];
    const parkRows = data.filter(r => r.ABONENO === selectedPark.ABONENO);
    const byDonem = {};
    parkRows.forEach(r => {
      const d = String(r.DONEM).replace('.0','').replace(/\..*/,'');
      const val = parseFloat(r.M3_TUKETIM) || 0;
      if (d && d.length >= 6) {
        if (!byDonem[d] || val > byDonem[d]) byDonem[d] = val;
      }
    });
    return Object.entries(byDonem)
      .sort(([a],[b]) => a.localeCompare(b))
      .map(([donem, tuketim]) => ({
        donem,
        donemLabel: formatDonem(donem),
        tuketim: Math.round(tuketim),
      }));
  }, [selectedPark, data]);

  const parkStats = useMemo(() => {
    if (!parkChartData.length) return null;
    const values = parkChartData.map(d => d.tuketim);
    const total = values.reduce((s,v) => s+v, 0);
    const avg = total / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);
    return { total, avg: Math.round(avg), max, min, count: values.length };
  }, [parkChartData]);

  const handleRowClick = (row) => {
    if (selectedPark && selectedPark.ABONENO === row.ABONENO) {
      setSelectedPark(null);
    } else {
      setSelectedPark(row);
    }
  };
  const metricItems = stats ? [
    { label: 'Kayit', value: stats.kayitSayisi.toLocaleString('tr-TR') },
    { label: 'Abone', value: stats.aboneler.length.toLocaleString('tr-TR') },
    { label: 'Ilce', value: stats.ilceler.length },
    { label: 'Tuketim', value: `${(stats.toplamTuketim/1000000).toFixed(1)}M m\u00B3` },
    { label: 'Donem', value: stats.donemler.length > 0 ? `${formatDonem(stats.donemler[0])}\u2013${formatDonem(stats.donemler[stats.donemler.length-1])}` : '-' },
  ] : [];

  return (
    <>
    <div style={{ display:'flex', gap:20, alignItems:'flex-start', fontFamily:'-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif' }}>
      {/* Sol: Ana icerik */}
      <div style={{ flex:1, minWidth:0 }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div>
            <h2 style={{ margin:0, color:C.text, fontSize:16, fontWeight:700, letterSpacing:'-0.01em' }}>
              ASKI Canli Veri
              {activeHistoryKey && <span style={{ color:C.accent, fontWeight:500, fontSize:12, marginLeft:8 }}>Gecmis goruntuluyor</span>}
            </h2>
            <p style={{ margin:'2px 0 0', color:C.textFaint, fontSize:11 }}>Park Bahce abonelik ve fatura verileri</p>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            {fetchedAt && <span style={{ fontSize:10, color:C.textMuted }}>{formatDate(fetchedAt)}</span>}
            <button onClick={handleFetchClick} disabled={loading||historyItemLoading||bgProcessing} style={{
              padding:'7px 18px', borderRadius:6, border:'none', fontSize:12, fontWeight:600, cursor:(loading||bgProcessing)?'not-allowed':'pointer', transition:'all .15s',
              background:(loading||bgProcessing)?C.border:C.accent, color:'#fff',
            }}>{loading?'Cekilyor...':(bgProcessing?'Arka planda...':'Veri Cek')}</button>
          </div>
        </div>

        {/* Alerts */}
        {bgProcessing&&(
          <Alert type="warn">Buyuk veri cekilyor, arka planda devam ediyor. Tamamlaninca otomatik yuklenecek... (her 10 saniyede kontrol ediliyor)</Alert>
        )}
        {showConfirm&&!loading&&!bgProcessing&&(
          <Alert type="warn" onClose={()=>setShowConfirm(false)}>
            <span>ASKI'ye yeni istek gonderilecek.</span>
            <button onClick={doFetch} style={alertBtn}>Evet</button>
            <button onClick={()=>setShowConfirm(false)} style={{...alertBtn,background:'transparent',color:C.textMuted,border:`1px solid ${C.border}`}}>Vazgec</button>
          </Alert>
        )}
        {error&&<Alert type="error"><strong>Hata:</strong> {error}</Alert>}
        {dbSyncInfo&&!loading&&<Alert type="success">{dbSyncInfo.total.toLocaleString('tr-TR')} kayit DB'ye yazildi{dbSyncInfo.inserted>0&&` (${dbSyncInfo.inserted} eklendi/guncellendi)`}</Alert>}
        {!dbSyncInfo&&data&&!loading&&fetchedAt&&!activeHistoryKey&&<Alert type="warn">Veri cekildi ancak DB'ye yazilamadi. Lambda guncellenmis olabilir mi kontrol edin.</Alert>}

        {/* Empty / Loading */}
        {!data&&!loading&&!error&&!historyItemLoading&&(
          <div style={{ textAlign:'center', padding:'60px 20px', color:C.textFaint, fontSize:12 }}>
            <div style={{ width:48,height:48,borderRadius:12,background:C.accentLight,margin:'0 auto 12px',display:'flex',alignItems:'center',justifyContent:'center' }}>
              <svg width="20" height="20" fill="none" stroke={C.accent} strokeWidth="1.5" viewBox="0 0 24 24"><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"/></svg>
            </div>
            <p style={{ fontWeight:600, color:C.textMuted, margin:'0 0 4px' }}>Henuz veri yok</p>
            <p style={{ margin:0 }}>"Veri Cek" ile baslayabilir veya gecmis cekim secebilirsiniz.</p>
          </div>
        )}
        {(loading||historyItemLoading)&&(
          <div style={{ textAlign:'center', padding:'48px 20px', color:C.textMuted, fontSize:12 }}>
            <div style={{ width:32,height:32,border:`3px solid ${C.border}`,borderTopColor:C.accent,borderRadius:'50%',margin:'0 auto 12px',animation:'spin 0.8s linear infinite' }}/>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            <p style={{ margin:0 }}>{historyItemLoading?'Gecmis veri yukleniyor...':'ASKI servisine baglaniliyor...'}</p>
          </div>
        )}

        {/* Metric strip */}
        {stats&&(
          <div style={{ display:'flex', gap:1, background:C.border, borderRadius:8, overflow:'hidden', marginBottom:14 }}>
            {metricItems.map((m,i)=>(
              <div key={i} style={{ flex:1, background:C.card, padding:'10px 12px', textAlign:'center' }}>
                <div style={{ fontSize:15, fontWeight:700, color:C.text }}>{m.value}</div>
                <div style={{ fontSize:9, color:C.textFaint, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em', marginTop:2 }}>{m.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Data table */}
        {data&&data.length>0&&(
          <div style={{ background:C.card, borderRadius:8, border:`1px solid ${C.border}`, overflow:'hidden' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', borderBottom:`1px solid ${C.borderLight}`, background:C.bg }}>
              <svg width="14" height="14" fill="none" stroke={C.textFaint} strokeWidth="1.5" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/></svg>
              <input type="text" placeholder="Ara..." value={search} onChange={e=>setSearch(e.target.value)}
                style={{ padding:'5px 0', border:'none', background:'transparent', fontSize:12, color:C.text, outline:'none', flex:1 }}/>
              <select value={filterIlce} onChange={e=>setFilterIlce(e.target.value)}
                style={{ padding:'4px 6px', borderRadius:4, border:`1px solid ${C.border}`, fontSize:11, background:C.card, color:C.text, cursor:'pointer' }}>
                <option value="">Tum Ilceler</option>
                {stats&&stats.ilceler.sort().map(i=><option key={i} value={i}>{i}</option>)}
              </select>
              <span style={{ fontSize:10, color:C.textFaint }}>{filtered.length}/{data.length}</span>
              <button onClick={()=>setShowRaw(!showRaw)} style={{ padding:'3px 8px', borderRadius:4, border:`1px solid ${C.border}`, background:showRaw?C.accentLight:C.card, color:showRaw?C.accent:C.textMuted, fontSize:10, cursor:'pointer', fontWeight:600 }}>
                {showRaw?'Tablo':'JSON'}
              </button>
            </div>
            {showRaw?(
              <pre style={{ background:'#0f172a', color:'#e2e8f0', padding:12, margin:0, fontSize:10, maxHeight:500, overflow:'auto', whiteSpace:'pre-wrap', wordBreak:'break-all' }}>
                {JSON.stringify(data,null,2)}
              </pre>
            ):(
              <div style={{ overflowX:'auto', maxHeight:520 }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                  <thead>
                    <tr>{['#',...TABLE_COLUMNS].map((col,ci)=>(
                      <th key={ci} onClick={()=>ci>0&&handleSort(col)} style={{ padding:'7px 8px', textAlign:'left', borderBottom:`2px solid ${C.border}`, background:C.bg, color:C.textMuted, fontWeight:600, fontSize:10, whiteSpace:'nowrap', position:'sticky', top:0, zIndex:1, cursor:ci>0?'pointer':'default', userSelect:'none' }}>
                        {ci===0?'#':(COLUMN_LABELS[col]||col)}{sortCol===col&&<span style={{marginLeft:2,fontSize:8}}>{sortDir==='asc'?'\u25B2':'\u25BC'}</span>}
                      </th>
                    ))}</tr>
                  </thead>
                  <tbody>{filtered.map((row,i)=>(
                    <tr key={i} onClick={()=>handleRowClick(row)} style={{ background: selectedPark && selectedPark.ABONENO===row.ABONENO ? '#dbeafe' : i%2===0?C.card:'#fafbfd', cursor:'pointer' }} onMouseEnter={e=>{if(!(selectedPark&&selectedPark.ABONENO===row.ABONENO))e.currentTarget.style.background=C.accentBg}} onMouseLeave={e=>{if(!(selectedPark&&selectedPark.ABONENO===row.ABONENO))e.currentTarget.style.background=i%2===0?C.card:'#fafbfd'}}>
                      <td style={{...td,color:C.textFaint,fontSize:9,width:28,textAlign:'center'}}>{i+1}</td>
                      {TABLE_COLUMNS.map(col=>(
                        <td key={col} style={{...td,fontWeight:col==='M3_TUKETIM'?600:400,color:col==='M3_TUKETIM'?(parseFloat(row[col])>100?C.red:C.green):C.text}}>
                          {hl(cellValue(row,col),search)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {filtered.length===0&&<tr><td colSpan={TABLE_COLUMNS.length+1} style={{textAlign:'center',padding:24,color:C.textFaint,fontSize:12}}>Sonuc bulunamadi.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Park Zaman Serisi Grafigi */}
        {selectedPark && parkChartData.length > 0 && (
          <div style={{ background:C.card, borderRadius:10, border:`1px solid ${C.border}`, padding:20, marginTop:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div>
                <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:C.text }}>
                  {selectedPark.ABONE_ADI || selectedPark.ABONENO}
                </h3>
                <p style={{ margin:'2px 0 0', fontSize:11, color:C.textMuted }}>
                  {selectedPark.ILCE_ADI} • {selectedPark.MAHALLE_ADI} • Abone: {selectedPark.ABONENO}
                </p>
              </div>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <button onClick={()=>setChartType('line')} style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${chartType==='line'?C.accent:C.border}`, background:chartType==='line'?C.accentLight:C.card, color:chartType==='line'?C.accent:C.textMuted, fontSize:10, fontWeight:600, cursor:'pointer' }}>Cizgi</button>
                <button onClick={()=>setChartType('bar')} style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${chartType==='bar'?C.accent:C.border}`, background:chartType==='bar'?C.accentLight:C.card, color:chartType==='bar'?C.accent:C.textMuted, fontSize:10, fontWeight:600, cursor:'pointer' }}>Cubuk</button>
                <button onClick={()=>setSelectedPark(null)} style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${C.border}`, background:C.card, color:C.red, fontSize:10, fontWeight:600, cursor:'pointer' }}>Kapat</button>
              </div>
            </div>
            {parkStats && (
              <div style={{ display:'flex', gap:1, background:C.border, borderRadius:6, overflow:'hidden', marginBottom:14 }}>
                <div style={{ flex:1, background:C.card, padding:'8px 10px', textAlign:'center' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.accent }}>{parkStats.count}</div>
                  <div style={{ fontSize:8, color:C.textFaint, fontWeight:600, textTransform:'uppercase' }}>Donem</div>
                </div>
                <div style={{ flex:1, background:C.card, padding:'8px 10px', textAlign:'center' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{parkStats.avg.toLocaleString('tr-TR')} m\u00B3</div>
                  <div style={{ fontSize:8, color:C.textFaint, fontWeight:600, textTransform:'uppercase' }}>Ortalama</div>
                </div>
                <div style={{ flex:1, background:C.card, padding:'8px 10px', textAlign:'center' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.red }}>{parkStats.max.toLocaleString('tr-TR')} m\u00B3</div>
                  <div style={{ fontSize:8, color:C.textFaint, fontWeight:600, textTransform:'uppercase' }}>Maksimum</div>
                </div>
                <div style={{ flex:1, background:C.card, padding:'8px 10px', textAlign:'center' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.green }}>{parkStats.min.toLocaleString('tr-TR')} m\u00B3</div>
                  <div style={{ fontSize:8, color:C.textFaint, fontWeight:600, textTransform:'uppercase' }}>Minimum</div>
                </div>
                <div style={{ flex:1, background:C.card, padding:'8px 10px', textAlign:'center' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.amber }}>{parkStats.total.toLocaleString('tr-TR')} m\u00B3</div>
                  <div style={{ fontSize:8, color:C.textFaint, fontWeight:600, textTransform:'uppercase' }}>Toplam</div>
                </div>
              </div>
            )}
            <div style={{ width:'100%', height:280 }}>
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'line' ? (
                  <LineChart data={parkChartData} margin={{ top:5, right:20, bottom:40, left:10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} />
                    <XAxis dataKey="donemLabel" tick={{ fontSize:9, fill:C.textMuted }} angle={-45} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize:10, fill:C.textMuted }} tickFormatter={v=>`${v} m\u00B3`} />
                    <Tooltip formatter={(v) => [`${v.toLocaleString('tr-TR')} m\u00B3`, 'Tuketim']} labelStyle={{ fontWeight:700 }} />
                    <Line type="monotone" dataKey="tuketim" stroke={C.accent} strokeWidth={2.5} dot={{ r:3, fill:C.accent }} activeDot={{ r:5 }} />
                  </LineChart>
                ) : (
                  <BarChart data={parkChartData} margin={{ top:5, right:20, bottom:40, left:10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} />
                    <XAxis dataKey="donemLabel" tick={{ fontSize:9, fill:C.textMuted }} angle={-45} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize:10, fill:C.textMuted }} tickFormatter={v=>`${v} m\u00B3`} />
                    <Tooltip formatter={(v) => [`${v.toLocaleString('tr-TR')} m\u00B3`, 'Tuketim']} labelStyle={{ fontWeight:700 }} />
                    <Bar dataKey="tuketim" fill={C.accent} radius={[4,4,0,0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
            <p style={{ margin:'10px 0 0', fontSize:10, color:C.textFaint, textAlign:'center' }}>
              Tablodaki satira tiklayarak baska parklar secebilirsiniz. Tekrar tiklayarak grafigi kapatabilirsiniz.
            </p>
          </div>
        )}
      </div>

      {/* Sag: Sidebar */}
      <div style={{ width:240, flexShrink:0, display:'flex', flexDirection:'column', gap:12 }}>
        {/* DB Durumu */}
        <SidePanel title="Veritabani Durumu" accent={C.green}>
          {dbStatus&&dbStatus.summary?(
            <div style={{ fontSize:11, color:C.textMuted }}>
              <Row label="Toplam kayit" value={(dbStatus.summary.total||0).toLocaleString('tr-TR')} />
              <Row label="Farkli abone" value={(dbStatus.summary.unique_abone||0).toLocaleString('tr-TR')} />
              <Row label="Donem" value={dbStatus.summary.min_donem?`${formatDonem(dbStatus.summary.min_donem)} \u2013 ${formatDonem(dbStatus.summary.max_donem)}`:'-'} />
              <Row label="Son cekim" value={dbStatus.summary.last_fetch?formatDate(dbStatus.summary.last_fetch):'-'} small />
              {dbStatus.bySources&&dbStatus.bySources.length>0&&(
                <div style={{ marginTop:8, paddingTop:8, borderTop:`1px solid ${C.borderLight}` }}>
                  {dbStatus.bySources.map(s=>(
                    <div key={s.fetch_source} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:3, fontSize:10 }}>
                      <span style={{ display:'flex', alignItems:'center', gap:4 }}>
                        <span style={{ width:6,height:6,borderRadius:'50%',background:s.fetch_source==='auto'?C.amber:C.accent }}/>
                        {s.fetch_source==='auto'?'Otomatik':'Manuel'}
                      </span>
                      <strong>{s.cnt}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ):<p style={{fontSize:11,color:C.textFaint,margin:0}}>Henuz veri yok</p>}
        </SidePanel>

        {/* Gecmis */}
        <SidePanel title="Cekim Gecmisi" accent={C.accent} action={<button onClick={()=>{loadHistory();loadDbStatus();}} disabled={historyLoading} style={{padding:'2px 6px',borderRadius:3,border:`1px solid ${C.border}`,background:C.card,fontSize:9,cursor:'pointer',color:C.textMuted}}>{historyLoading?'...':'\u21BB'}</button>}>
          <div style={{ maxHeight:360, overflowY:'auto', margin:'0 -12px', padding:'0' }}>
            {historyList.length===0&&!historyLoading&&<p style={{fontSize:10,color:C.textFaint,textAlign:'center',padding:'12px 0',margin:0}}>Henuz cekim yok</p>}
            {historyLoading&&<p style={{fontSize:10,color:C.textFaint,textAlign:'center',padding:'12px 0',margin:0}}>Yukleniyor...</p>}
            {historyList.map((item,idx)=>{
              const isAuto=item.label&&item.label.startsWith('auto-');
              const isActive=activeHistoryKey===item.key;
              return(
                <button key={item.key} onClick={()=>loadHistoryItem(item)} style={{
                  display:'flex',alignItems:'center',gap:8,width:'100%',textAlign:'left',padding:'8px 12px',
                  border:'none',borderBottom:`1px solid ${C.borderLight}`,cursor:'pointer',
                  background:isActive?C.accentBg:idx%2===0?C.card:'#fafbfd',transition:'background .1s',
                }}>
                  <div style={{ width:6,height:6,borderRadius:'50%',background:isAuto?C.amber:C.accent,flexShrink:0 }}/>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontSize:10,fontWeight:600,color:isActive?C.accent:C.text,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis' }}>
                      {formatDate(item.date)}
                    </div>
                    <div style={{ fontSize:9,color:C.textFaint }}>{formatSize(item.size)} &middot; {isAuto?'Otomatik':'Manuel'}</div>
                  </div>
                  {isActive&&<div style={{width:4,height:4,borderRadius:'50%',background:C.accent}}/>}
                </button>
              );
            })}
          </div>
        </SidePanel>
      </div>
    </div>

    {/* Veri Cikarimlari & Analiz Potansiyeli */}
    <InsightsPanel />
    </>
  );
};

const INSIGHTS_DATA = [
  {
    id: 'water',
    title: 'Su Tuketim Analizi',
    status: 'mevcut',
    statusColor: C.green,
    items: [
      'Yesil alan basi su tuketimi hesaplama (m\u00B3/m\u00B2)',
      'Donemsel tuketim karsilastirmasi (aylik/mevsimlik trend)',
      'Ilce bazli tuketim dagilimi ve karsilastirmasi',
      'Abone tipi bazli tuketim analizi',
      'Sayac turu/capi ile tuketim korelasyonu',
    ],
  },
  {
    id: 'anomaly',
    title: 'Anomali Tespiti',
    status: 'mevcut',
    statusColor: C.amber,
    items: [
      'Beklenenden yuksek/dusuk tuketim gosteren parklar',
      'Donemler arasi ani artis/azalis tespiti',
      'Ilce ortalamasindan sapma analizi',
    ],
  },
  {
    id: 'meteo',
    title: 'Meteoroloji Korelasyonu',
    status: 'veri mevcut',
    statusColor: C.green,
    items: [
      'ET0 (referans evapotranspirasyon) hesaplama (FAO Penman-Monteith)',
      'Sicaklik-tuketim korelasyonu',
      'Mevsimsel sulama ihtiyaci tahmini',
      'Ruzgar hizi ile buharlasma etkisi analizi',
    ],
  },
  {
    id: 'ndvi',
    title: 'NDVI / Uydu Analizi',
    status: 'GEE gerekli',
    statusColor: C.red,
    items: [
      'Bitki sagligi endeksi (NDVI skoru)',
      'Yesil alan verimlilik skoru = Tuketim / NDVI',
      'Zamansal degisim: Yesil alan kazanimi/kaybi',
      'Kuraklik risk haritasi',
      'Tipoloji bazli su ihtiyaci (cim vs cali vs agac)',
    ],
  },
  {
    id: 'cross',
    title: 'Capraz Analiz (Tum Kaynaklar)',
    status: 'gelecek',
    statusColor: C.textFaint,
    items: [
      'Su Verimlilik Endeksi = (NDVI x Alan) / Tuketim',
      'Yuksek tuketim + dusuk NDVI = su kaybi/sizinti tespiti',
      'Meteoroloji + NDVI + Tuketim birlesiik mevsimsel optimizasyon',
      'Sulama programi onerisi: ET0 + bitki katsayisi + toprak nemi',
      'Gelecek ay tahmini tuketim (trend + hava durumu)',
    ],
  },
];

const MISSING_DATA = [
  { name: 'ASKI abonelik/fatura', status: 'Mevcut', color: C.green, detail: '~9.744 kayit, otomatik guncelleniyor' },
  { name: 'Meteoroloji verileri', status: 'Mevcut', color: C.green, detail: '~47M kayit (sicaklik, nem, ruzgar, radyasyon)' },
  { name: 'ET0 hesaplamalari', status: 'Mevcut', color: C.green, detail: '~147K kayit' },
  { name: 'Park alanlari (park_area)', status: 'Bos', color: C.amber, detail: 'Tablo var, veri yok' },
  { name: 'Park sulama (park_irrigation)', status: 'Bos', color: C.amber, detail: 'Tablo var, veri yok' },
  { name: 'NDVI/uydu goruntuleri', status: 'Eksik', color: C.red, detail: 'GEE baglantisi henuz yok' },
  { name: 'Toprak nemi sensoru', status: 'Eksik', color: C.red, detail: 'Hassas sulama, anomali tespiti' },
  { name: 'Yagis verisi', status: 'Eksik', color: C.red, detail: 'Dogal sulama hesabi, tuketim duzeltmesi' },
];

const InsightsPanel = () => {
  const [open, setOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState('analysis');

  return (
    <div style={{ marginTop: 20, background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, overflow: 'hidden', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', background: C.bg, border: 'none', borderBottom: open ? `1px solid ${C.borderLight}` : 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Veri Cikarimlari ve Analiz Potansiyeli</span>
        <span style={{ fontSize: 10, color: C.textFaint, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>{'\u25BC'}</span>
      </button>

      {open && (
        <div style={{ padding: 16 }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${C.borderLight}`, paddingBottom: 8 }}>
            {[{ id: 'analysis', label: 'Neler Yapilabilir?' }, { id: 'missing', label: 'Veri Durumu' }].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                style={{
                  padding: '5px 14px', borderRadius: 4, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  background: activeSection === tab.id ? C.accent : 'transparent',
                  color: activeSection === tab.id ? '#fff' : C.textMuted,
                  transition: 'all .15s',
                }}
              >{tab.label}</button>
            ))}
          </div>

          {activeSection === 'analysis' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {INSIGHTS_DATA.map(section => (
                <div key={section.id} style={{ background: C.bg, borderRadius: 6, border: `1px solid ${C.borderLight}`, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{section.title}</span>
                    <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: `${section.statusColor}18`, color: section.statusColor, border: `1px solid ${section.statusColor}30` }}>{section.status}</span>
                  </div>
                  <ul style={{ margin: 0, padding: '0 0 0 14px', fontSize: 11, color: C.textMuted, lineHeight: 1.8 }}>
                    {section.items.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {activeSection === 'missing' && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                    <th style={{ textAlign: 'left', padding: '8px 10px', color: C.textMuted, fontWeight: 600, fontSize: 11 }}>Veri Kaynagi</th>
                    <th style={{ textAlign: 'center', padding: '8px 10px', color: C.textMuted, fontWeight: 600, fontSize: 11 }}>Durum</th>
                    <th style={{ textAlign: 'left', padding: '8px 10px', color: C.textMuted, fontWeight: 600, fontSize: 11 }}>Detay</th>
                  </tr>
                </thead>
                <tbody>
                  {MISSING_DATA.map((row, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${C.borderLight}` }}>
                      <td style={{ padding: '8px 10px', fontWeight: 500, color: C.text }}>{row.name}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 10px', borderRadius: 10, background: `${row.color}18`, color: row.color, border: `1px solid ${row.color}30` }}>{row.status}</span>
                      </td>
                      <td style={{ padding: '8px 10px', color: C.textMuted, fontSize: 11 }}>{row.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SidePanel=({title,accent,action,children})=>(
  <div style={{background:C.card,borderRadius:8,border:`1px solid ${C.border}`,overflow:'hidden'}}>
    <div style={{padding:'10px 12px',borderBottom:`1px solid ${C.borderLight}`,borderLeft:`3px solid ${accent}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
      <span style={{fontSize:12,fontWeight:700,color:C.text}}>{title}</span>
      {action}
    </div>
    <div style={{padding:12}}>{children}</div>
  </div>
);

const Row=({label,value,small})=>(
  <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
    <span>{label}</span>
    <strong style={{color:C.text,fontSize:small?9:11}}>{value}</strong>
  </div>
);

const Alert=({type,children,onClose})=>{
  const colors={error:{bg:C.redBg,border:'#fca5a5',text:C.red},warn:{bg:C.amberBg,border:'#fde68a',text:'#92400e'},success:{bg:C.greenBg,border:'#a7f3d0',text:C.green}};
  const c=colors[type]||colors.success;
  return(<div style={{display:'flex',alignItems:'center',gap:8,background:c.bg,border:`1px solid ${c.border}`,padding:'7px 12px',borderRadius:6,marginBottom:10,fontSize:11,color:c.text}}>{children}</div>);
};

const alertBtn={padding:'3px 10px',borderRadius:4,border:'none',background:C.amber,color:'#fff',fontWeight:600,cursor:'pointer',fontSize:10};

const td={padding:'5px 8px',borderBottom:`1px solid ${C.borderLight}`,color:C.text,fontSize:11,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'};

const hl=(text,query)=>{if(!query.trim())return text;const idx=text.toLowerCase().indexOf(query.toLowerCase());if(idx===-1)return text;return(<>{text.substring(0,idx)}<mark style={{background:'#fef08a',padding:'0 1px',borderRadius:2}}>{text.substring(idx,idx+query.length)}</mark>{text.substring(idx+query.length)}</>);};

export default ASKILiveData;
